import { logGreyError } from 'owa-analytics';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import isRetriableAuthError from 'owa-service/lib/isRetriableAuthError';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import sendMsGraphRequest from 'accelerator-msgraph/lib/sendMsGraphRequest';

import type { MailboxInfo } from 'owa-client-types';
import type { GraphUsersResponse } from '../types/GraphUsersResponse';

interface UpnUsersResponse {
    id: string;
    userPrincipalName: string;
}

interface MsGraphList<T> {
    '@odata.nextLink': string | undefined;
    value: T[];
}

interface IHandleFetchWithRetryOptions {
    apiName: string;
    requestUrl: string;
    eventualConsistency: boolean;
    requestId?: string;
}

const MAX_RETRY_COUNT = 1;

export async function fetchIdsFromMsGraph(
    smtpQueryString: string,
    correlationId: string
): Promise<GraphUsersResponse[]> {
    const mailboxInfo = getModuleContextMailboxInfo();

    if (isGlobalFeatureEnabled('msplaces-fetchIdFromGraphWithUserPrincipalName')) {
        return fetchIdsWithUpnSupport(mailboxInfo, smtpQueryString, correlationId);
    }

    // Original logic: query by mail only
    const GET_MSGRAPH_SEARCH_URL: string = `v1.0/users?$select=id,mail&$count=true&$filter=mail+in(${smtpQueryString})`;

    const options: IHandleFetchWithRetryOptions = {
        apiName: 'idFetchFromMSGraph',
        requestUrl: GET_MSGRAPH_SEARCH_URL,
        eventualConsistency: true,
        requestId: correlationId,
    };
    return handleFetchWithRetry<GraphUsersResponse[]>(mailboxInfo, options, MAX_RETRY_COUNT);
}

async function fetchIdsWithUpnSupport(
    mailboxInfo: MailboxInfo,
    smtpQueryString: string,
    correlationId: string
): Promise<GraphUsersResponse[]> {
    // Query by both mail and userPrincipalName since the input could be either
    const mailSearchUrl = `v1.0/users?$select=id,mail&$count=true&$filter=mail+in(${smtpQueryString})`;
    const upnSearchUrl = `v1.0/users?$select=id,userPrincipalName&$count=true&$filter=userPrincipalName+in(${smtpQueryString})`;

    const mailOptions: IHandleFetchWithRetryOptions = {
        apiName: 'idFetchFromMSGraph_mail',
        requestUrl: mailSearchUrl,
        eventualConsistency: true,
        requestId: correlationId,
    };

    const upnOptions: IHandleFetchWithRetryOptions = {
        apiName: 'idFetchFromMSGraph_upn',
        requestUrl: upnSearchUrl,
        eventualConsistency: true,
        requestId: correlationId,
    };

    // Execute both queries in parallel
    const [mailResults, upnResults] = await Promise.all([
        handleFetchWithRetry<GraphUsersResponse[]>(mailboxInfo, mailOptions, MAX_RETRY_COUNT).catch(
            () => [] as GraphUsersResponse[]
        ),
        handleFetchWithRetry<UpnUsersResponse[]>(mailboxInfo, upnOptions, MAX_RETRY_COUNT).catch(
            () => [] as UpnUsersResponse[]
        ),
    ]);

    // Convert userPrincipalName results to match GraphUsersResponse interface
    const convertedUpnResults: GraphUsersResponse[] = upnResults.map(user => ({
        id: user.id,
        mail: user.userPrincipalName,
    }));

    // Merge results, preferring mail results over UPN results for the same id
    const resultMap = new Map<string, GraphUsersResponse>();
    for (const user of convertedUpnResults) {
        resultMap.set(user.id, user);
    }
    for (const user of mailResults) {
        resultMap.set(user.id, user);
    }

    return Array.from(resultMap.values());
}

async function handleFetchWithRetry<T>(
    mailboxInfo: MailboxInfo,
    options: IHandleFetchWithRetryOptions,
    retryCount: number = MAX_RETRY_COUNT
): Promise<T> {
    return sendMsGraphRequest<MsGraphList<T>>(mailboxInfo, {
        apiName: options.apiName,
        method: 'GET',
        requestUrl: options.requestUrl,
        eventualConsistency: options.eventualConsistency,
        requestId: options.requestId,
    })
        .then(response => {
            return response.value as T; // returning array that can be empty
        })
        .catch(error => {
            if (
                isRetriableAuthError(
                    error?.response?.status,
                    true /** authNeededOnUnAuthorized */
                ) &&
                retryCount > 0
            ) {
                return handleFetchWithRetry<T>(mailboxInfo, options, retryCount - 1);
            }

            logGreyError('PresenceServiceFetchIdsFromMsGraph_FetchError', mailboxInfo, error, {
                apiEndpoint: options.requestUrl,
                retryCount,
                correlationId: error?.response?.headers?.get('x-ms-correlation-id'),
                cV: error?.response?.headers?.get('ms-cv'),
            });

            throw error;
        });
}
